/* assets/wishlist.js
   - Wishlist localStorage
   - Wishlist page render via section_id=wbs-wishlist-card (extracts ONLY li.grid__item)
   - Heart toggle everywhere
   - Remove + Share
   - Move to cart: adds first available variant, passes cart section IDs to /cart/add.js,
     then dispatches cart:update (same contract as product-form / FBT) so cart-drawer-component
     and cart-icon morph/update without a full page reload.
   - Wishlist badge hidden when 0 (no "0" shown)
*/

(() => {
  // Theme + block both render assets — only initialize once.
  if (window.DawnWishlist) return;

  const KEY = "sovi:wishlist:v1";
  const LEGACY_KEYS = ["pinca:wishlist:v1", "wbs_wishlist", "wishlist", "dawn:wishlist"];
  const EVENT = "wishlist:changed";

  // ---------------------------
  // Storage
  // ---------------------------
  const safeJson = (raw) => {
    try { return JSON.parse(raw); } catch { return null; }
  };

  const migrateLegacy = () => {
    if (localStorage.getItem(KEY)) return;
    for (const legacy of LEGACY_KEYS) {
      const arr = safeJson(localStorage.getItem(legacy));
      if (Array.isArray(arr) && arr.length) {
        localStorage.setItem(KEY, JSON.stringify(arr.filter(Boolean)));
        return;
      }
    }
  };

  const read = () => {
    migrateLegacy();
    const arr = safeJson(localStorage.getItem(KEY));
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  };

  const write = (items) => {
    const clean = Array.from(new Set((items || []).filter(Boolean)));
    localStorage.setItem(KEY, JSON.stringify(clean));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { items: clean } }));
    return clean;
  };

  const removeHandle = (handle) => write(read().filter((h) => h !== handle));

  const toggleHandle = (handle) => {
    const items = read();
    if (items.includes(handle)) {
      return { items: write(items.filter((h) => h !== handle)), action: "removed" };
    }
    return { items: write([...items, handle]), action: "added" };
  };

  // ---------------------------
  // Toast
  // ---------------------------
  let toastEl = null;
  let toastTimer = null;

  const ensureToast = () => {
    if (toastEl) return toastEl;
    toastEl = document.createElement("div");
    toastEl.className = "wishlist-toast";
    toastEl.setAttribute("role", "status");
    toastEl.setAttribute("aria-live", "polite");
    document.body.appendChild(toastEl);
    return toastEl;
  };

  const showToast = (msg) => {
    const el = ensureToast();
    el.textContent = msg;
    el.classList.remove("is-visible");
    void el.offsetWidth;
    el.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => el.classList.remove("is-visible"), 1400);
  };

  // ---------------------------
  // Sync UI (hearts + wishlist count)
  // ---------------------------
  const setBtnState = (btn, active) => {
    btn.classList.toggle("is-active", !!active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    btn.setAttribute(
      "aria-label",
      active ? "Remove from wishlist" : "Add to wishlist"
    );
  };

  // Hide badge when 0 and don't show "0". Header uses cart-bubble markup.
  const syncWishlistBadge = (items) => {
    const count = items.length;

    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      const textEl =
        el.querySelector("[data-wishlist-count-text]") ||
        el.querySelector(".cart-bubble__text-count") ||
        el;
      const host = el.closest(".header-actions__wishlist");

      if (count <= 0) {
        textEl.textContent = "";
        el.setAttribute("data-count", "0");
        el.hidden = true;
        el.setAttribute("aria-hidden", "true");
        if (host) host.classList.remove("header-actions__wishlist--has-items");
        return;
      }

      textEl.textContent = count < 100 ? String(count) : "";
      el.setAttribute("data-count", String(count));
      el.toggleAttribute("data-maintain-ratio", count <= 99);
      el.hidden = false;
      el.setAttribute("aria-hidden", "false");
      if (host) host.classList.add("header-actions__wishlist--has-items");
    });
  };

  const sync = (root = document) => {
    const items = read();
    const scope = root && root.querySelectorAll ? root : document;

    scope.querySelectorAll("[data-wishlist-btn]").forEach((btn) => {
      const handle = btn.getAttribute("data-handle");
      if (!handle) return;
      setBtnState(btn, items.includes(handle));
    });

    // keep header badge correct
    syncWishlistBadge(items);
  };

  // Section IDs for cart-items-component (header + cart page). Required so /cart/add.js returns
  // section HTML and cart-items / header morph on cart:update (see product-form.js, fbt.liquid).
  const getCartItemsSectionIds = () =>
    Array.from(document.querySelectorAll("cart-items-component[data-section-id]"))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(",");

  // ---------------------------
  // Wishlist page render (extract only LI)
  // ---------------------------
  const renderWishlistPage = async () => {
    const page = document.querySelector("[data-wishlist-page]");
    if (!page) return;

    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    if (!grid) return;

    const items = read();

    if (!items.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    const parts = await Promise.all(
      items.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=wbs-wishlist-card`, {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!res.ok) return "";
          const html = await res.text();

          const doc = new DOMParser().parseFromString(html, "text/html");
          const li = doc.querySelector("li.grid__item");
          return li ? li.outerHTML : "";
        } catch {
          return "";
        }
      })
    );

    grid.innerHTML = parts.filter(Boolean).join("");
    sync(page);
  };

  // ---------------------------
  // Share wishlist
  // ---------------------------
  const getShareUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("wl", read().join(","));
    return url.toString();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    }
  };

  const importFromUrlOnce = () => {
    const url = new URL(window.location.href);
    const wl = url.searchParams.get("wl");
    if (!wl) return;

    const handles = wl.split(",").map((s) => s.trim()).filter(Boolean);
    if (!handles.length) return;

    write(handles);

    url.searchParams.delete("wl");
    window.history.replaceState({}, "", url.toString());
    showToast("Wishlist loaded");
  };

  // ---------------------------
  // Move to cart
  // ---------------------------
  const getFirstAvailableVariantId = async (handle) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    });
    if (!res.ok) throw new Error("Product JSON fetch failed");

    const p = await res.json();
    const v = (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
    if (!v) throw new Error("No variants found");
    return v.id;
  };

  const addToCart = async (variantId, qty = 1) => {
    const sections = getCartItemsSectionIds();
    const cartAddUrl = window?.Theme?.routes?.cart_add_url || "/cart/add.js";
    const body = { id: variantId, quantity: qty };
    if (sections) body.sections = sections;

    const res = await fetch(cartAddUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  };

  // ---------------------------
  // Click handlers
  // ---------------------------
  document.addEventListener("click", async (e) => {
    const btnHeart = e.target.closest("[data-wishlist-btn]");
    const btnRemove = e.target.closest("[data-wishlist-remove]");
    const btnMove = e.target.closest("[data-wishlist-move-to-cart]");
    const btnShare = e.target.closest("[data-wishlist-share]");

    // Heart toggle
    if (btnHeart) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnHeart.getAttribute("data-handle");
      if (!handle) return;

      const res = toggleHandle(handle);
      // write() already dispatches EVENT → sync all hearts; keep clicked btn snappy
      setBtnState(btnHeart, res.action === "added");
      showToast(res.action === "added" ? "Added to wishlist" : "Removed from wishlist");
      return;
    }

    // Remove
    if (btnRemove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnRemove.getAttribute("data-handle");
      if (!handle) return;

      removeHandle(handle);
      showToast("Removed from wishlist");
      return;
    }

    // Move to cart
    if (btnMove) {
      e.preventDefault();
      e.stopPropagation();

      const handle = btnMove.getAttribute("data-handle");
      if (!handle) return;

      const originalText = btnMove.textContent;
      btnMove.disabled = true;
      btnMove.textContent = "Adding...";

      try {
        const variantId = await getFirstAvailableVariantId(handle);
        const response = await addToCart(variantId, 1);

        // Theme cart-drawer (cart-drawer-component) + cart-items listen for cart:update with sections HTML.
        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: {
              resource: {},
              sourceId: "wishlist",
              data: {
                source: "product-form-component",
                itemCount: 1,
                sections: response.sections || {},
              },
            },
          })
        );

        const drawer = document.querySelector("cart-drawer-component");
        const drawerOpen = !!document.querySelector("cart-drawer-component dialog[open]");
        if (drawer && !drawerOpen) drawer.open?.();

        // remove from wishlist
        removeHandle(handle);

        showToast("Moved to cart");
      } catch (err) {
        console.error(err);
        showToast("Could not add to cart");
      } finally {
        btnMove.disabled = false;
        btnMove.textContent = originalText;
      }
      return;
    }

    // Share
    if (btnShare) {
      e.preventDefault();
      const url = getShareUrl();
      const ok = await copyToClipboard(url);
      showToast(ok ? "Wishlist link copied" : "Copy failed");
      return;
    }
  });

  // ---------------------------
  // Boot / events
  // ---------------------------
  const boot = () => {
    importFromUrlOnce();
    sync(document);
    renderWishlistPage();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener("pageshow", () => sync(document));

  window.addEventListener(EVENT, () => {
    sync(document);
    renderWishlistPage();
  });

  document.addEventListener("shopify:section:load", (e) => {
    sync(e.target || document);
    renderWishlistPage();
  });

  // Product recommendations / morph inject new hearts after initial boot
  let syncTimer = null;
  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(() => sync(document), 50);
  };

  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches?.("[data-wishlist-btn]") ||
            node.querySelector?.("[data-wishlist-btn]")
          ) {
            scheduleSync();
            return;
          }
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.DawnWishlist = {
    read, write, toggleHandle, removeHandle,
    sync, renderWishlistPage,
  };
})();
